import {
  ICreateBayGridPartProps,
  ICreateGridFromConfigProps,
  IGridPartDimensions,
} from "../../types/ICreateBayGridBaseProps";
import { ISlotCombinedPattern } from "@tedivo/tedivo-bay-grid-pure";
import { buildGridRowsAndTiers } from "./buildGridRowsAndTiers";

export function calculateGridPartDimensions<U>({
  tiersArray,
  rowsArray,
  containerWidth,
  containerHeight,
  displayNumbers,
  cellSeparation = 0,
}: Omit<ICreateBayGridPartProps<U>, "nodesOptions">): IGridPartDimensions {
  const addLeftWidth = Number(!!displayNumbers.left) * containerWidth,
    addRightWidth = Number(!!displayNumbers.right) * containerWidth,
    addTopHeight = Number(!!displayNumbers.top) * containerHeight,
    addBottomHeight = Number(!!displayNumbers.bottom) * containerHeight;

  const numberOfTiers = tiersArray.length,
    numberOfRows = rowsArray.length;

  const widthOfCells = containerWidth * numberOfRows + cellSeparation * (numberOfRows - 1),
    heightOfCells = containerHeight * numberOfTiers + cellSeparation * (numberOfTiers - 1);

  const widthOfGrid = addLeftWidth + widthOfCells + addRightWidth;
  const heightOfGrid = addTopHeight + heightOfCells + addBottomHeight;

  const cellPositions: {
    [pos: ISlotCombinedPattern]: { x: number; y: number };
  } = {};

  const tiersArraySortedDesc = tiersArray.sort(sortDesc);

  // Boxes for each cell
  for (let t = 0; t < numberOfTiers; t += 1) {
    for (let c = 0; c < numberOfRows; c += 1) {
      const name = `${rowsArray[c]}|${tiersArraySortedDesc[t]}` as ISlotCombinedPattern;

      const x = addLeftWidth + c * containerWidth + cellSeparation * c;
      const y = addTopHeight + t * containerHeight + cellSeparation * t;

      cellPositions[name] = { x, y };
    }
  }

  return {
    numberOfTiers,
    numberOfRows,
    addLeftWidth,
    addRightWidth,
    addTopHeight,
    addBottomHeight,
    widthOfCells,
    heightOfCells,
    widthOfGrid,
    heightOfGrid,
    cellPositions,
  };
}

export function calculateGridFromConfigDimensions<U>({
  minAboveTier,
  maxAboveTier,
  minBelowTier,
  maxBelowTier,
  centerLineRow,
  aboveCenterLineRow,
  belowCenterLineRow,
  maxRow,
  containerWidth,
  containerHeight,
  cellSeparation,
  belowGridSeparation,
}: Omit<ICreateGridFromConfigProps<U>, "nodesOptions">): ICalculatedGridDimensions {
  const { aboveRowsArray, belowRowsArray, aboveTiersArray, belowTiersArray } =
    buildGridRowsAndTiers({
      minAboveTier,
      maxAboveTier,
      minBelowTier,
      maxBelowTier,
      centerLineRow,
      aboveCenterLineRow,
      belowCenterLineRow,
      maxRow,
    });

  const aboveDimensions = aboveTiersArray
    ? calculateGridPartDimensions({
        tiersArray: aboveTiersArray,
        rowsArray: aboveRowsArray,
        containerWidth,
        containerHeight,
        displayNumbers: { left: true, right: true, top: true },
        cellSeparation,
      })
    : undefined;

  const belowDimensions = belowTiersArray
    ? calculateGridPartDimensions({
        tiersArray: belowTiersArray,
        rowsArray: belowRowsArray,
        containerWidth,
        containerHeight,
        displayNumbers: { left: true, right: true, bottom: true },
        cellSeparation,
      })
    : undefined;

  // Same layout as createBayGrid: the narrower part is centered
  const width = Math.max(aboveDimensions?.widthOfGrid ?? 0, belowDimensions?.widthOfGrid ?? 0);

  const cellPositions: ICellPositionsObject = {};
  let height = 0;

  if (aboveDimensions) {
    addOffsetCellPositions(
      aboveDimensions.cellPositions,
      (width - aboveDimensions.widthOfGrid) * 0.5,
      0,
      cellPositions,
    );
    height = aboveDimensions.heightOfGrid;
    if (belowDimensions) height += belowGridSeparation;
  }

  if (belowDimensions) {
    addOffsetCellPositions(
      belowDimensions.cellPositions,
      (width - belowDimensions.widthOfGrid) * 0.5,
      height,
      cellPositions,
    );
    height += belowDimensions.heightOfGrid;
  }

  return {
    width,
    height,
    cellPositions,
    aboveDimensions,
    belowDimensions,
  };
}

function addOffsetCellPositions(
  source: ICellPositionsObject,
  dx: number,
  dy: number,
  target: ICellPositionsObject,
): void {
  (Object.keys(source) as ISlotCombinedPattern[]).forEach((k) => {
    target[k] = { x: source[k].x + dx, y: source[k].y + dy };
  });
}

function sortDesc(a: string, b: string) {
  return Number(b) - Number(a);
}

export interface ICalculatedGridDimensions {
  width: number;
  height: number;
  cellPositions: ICellPositionsObject;
  aboveDimensions?: IGridPartDimensions;
  belowDimensions?: IGridPartDimensions;
}

export interface ICellPositionsObject {
  [pos: ISlotCombinedPattern]: { x: number; y: number };
}
